import React from 'react';
import { Stack, Text, Group, Paper } from '@mantine/core';
import {
  Sparkles,
  CheckSquare,
  StickyNote,
  Calendar,
  Kanban,
  ArrowRight,
  Lightbulb
} from 'lucide-react';
import { useNavigation } from '../../../../contexts/NavigationContext';
import { usePremiumFeatures } from '../../../../contexts/PremiumContext';
import { useUser } from '../../../../contexts/UserContext';
import { useDashboardData } from '../../../../hooks/dashboard';

export const OnboardingWelcome: React.FC = () => {
  const { setCurrentMode } = useNavigation();
  const { hasFeatureAccess } = usePremiumFeatures();
  const { currentUser } = useUser();
  const dashboardData = useDashboardData();

  const firstName = currentUser?.name ? currentUser.name.split(' ')[0] : null;

  // Check if user has some data but not enough for rich insights
  const hasAnyData = dashboardData.todos.length > 0 || dashboardData.todoLists.length > 0 || dashboardData.notes.length > 0;

  const navigationAreas = [
    {
      id: 'todos' as const,
      icon: CheckSquare,
      title: 'Todo Lists',
      description: 'Organize your tasks into projects and lists',
      isPremium: false,
      recommended: true
    },
    {
      id: 'notes' as const,
      icon: StickyNote,
      title: 'Notes',
      description: 'Quick thoughts, ideas, and reminders',
      isPremium: false,
      recommended: false
    },
    {
      id: 'calendar' as const,
      icon: Calendar,
      title: 'Calendar',
      description: 'Schedule and track your tasks over time',
      isPremium: true,
      recommended: false
    },
    {
      id: 'kanban' as const,
      icon: Kanban,
      title: 'Kanban Boards',
      description: 'Visual workflow management',
      isPremium: true,
      recommended: false
    }
  ];

  return (
    <Stack gap="xl" align="center" style={{
      textAlign: 'center',
      paddingTop: '2rem',
      maxWidth: '600px',
      margin: '0 auto'
    }}>
      {/* Welcome Header */}
      <Stack gap="md" align="center">
        <Sparkles size={48} style={{ color: 'var(--mantine-color-blue-6)', opacity: 0.8 }} />
        <Stack gap="xs" align="center">
          <Text size="xl" fw={700} style={{ color: 'var(--app-text-primary)' }}>
            {hasAnyData ? `Great start${firstName ? ` ${firstName}` : ''}! 🚀` : `Welcome${firstName ? ` ${firstName}` : ''} to Symphony Todo! 🎉`}
          </Text>
          <Text size="md" c="dimmed" ta="center" style={{ maxWidth: '500px' }}>
            {hasAnyData
              ? 'Keep exploring to unlock more dashboard insights! Add more tasks, lists, and notes to see productivity analytics and trends.'
              : 'This is your dashboard - it will show interesting insights about your projects, tasks, and productivity after you start using the app.'
            }
          </Text>
        </Stack>
      </Stack>

      {/* Navigation Areas */}
      <Stack gap="md" style={{ width: '100%', maxWidth: '500px' }}>
        <Group gap="xs" align="center" justify="center">
          <Lightbulb size={18} style={{ color: 'var(--mantine-color-orange-6)' }} />
          <Text size="sm" fw={500} c="dimmed">
            Explore these areas to get started:
          </Text>
        </Group>

        <Stack gap="sm">
          {navigationAreas.map((area) => {
            const Icon = area.icon;
            const hasAccess = !area.isPremium || hasFeatureAccess(area.id === 'kanban' ? 'KanbanBoards' : 'CalendarView');

            return (
              <Paper
                key={area.id}
                p="md"
                withBorder
                style={{
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  backgroundColor: area.recommended ? 'var(--mantine-color-blue-0)' : 'var(--app-surface)',
                  borderColor: area.recommended ? 'var(--mantine-color-blue-3)' : undefined,
                  opacity: hasAccess ? 1 : 0.7
                }}
                onClick={() => setCurrentMode(area.id)}
              >
                <Group gap="md" align="center" wrap="nowrap">
                  <Icon
                    size={24}
                    style={{
                      color: area.recommended ? 'var(--mantine-color-blue-6)' : 'var(--app-text-secondary)',
                      flexShrink: 0
                    }}
                  />
                  <Stack gap={2} style={{ flex: 1, textAlign: 'left' }}>
                    <Group gap="xs" align="center">
                      <Text size="sm" fw={600}>
                        {area.title}
                      </Text>
                      {area.recommended && (
                        <Text size="xs" c="blue" fw={600} style={{
                          backgroundColor: 'var(--mantine-color-blue-1)',
                          padding: '2px 6px',
                          borderRadius: '4px'
                        }}>
                          Start here
                        </Text>
                      )}
                      {!hasAccess && (
                        <Text size="xs" c="orange" fw={500} style={{
                          backgroundColor: 'var(--mantine-color-orange-1)',
                          padding: '2px 6px',
                          borderRadius: '4px'
                        }}>
                          Premium
                        </Text>
                      )}
                    </Group>
                    <Text size="xs" c="dimmed" style={{ lineHeight: 1.3 }}>
                      {area.description}
                    </Text>
                  </Stack>
                  <ArrowRight size={16} style={{ color: 'var(--app-text-muted)', flexShrink: 0 }} />
                </Group>
              </Paper>
            );
          })}
        </Stack>
      </Stack>


      {/* Additional Info */}
      <Text size="xs" c="dimmed" ta="center" style={{ maxWidth: '450px', marginTop: '1rem' }}>
        {hasAnyData
          ? 'Keep adding tasks and using Symphony Todo to unlock more dashboard widgets with statistics, upcoming deadlines, and productivity insights.'
          : 'Once you add some tasks and start using Symphony Todo, this dashboard will display helpful statistics, upcoming deadlines, and productivity insights.'
        }
      </Text>
    </Stack>
  );
};